import * as Sentry from "@sentry/react";
import { isAbortError } from "@/features/map/utils/isAbortError";

export type FetchZenrinTileResult =
  | { kind: "success"; objectUrl: string }
  | { kind: "aborted" }
  | { kind: "failed"; status?: number };

export interface FetchZenrinTileOptions {
  signal?: AbortSignal;
  headers: Array<{ header: string; value: string }> | undefined;
  oauthToken?: string;
  onTokenInvalid?: () => void;
}

/**
 * タイル画像用LRUキャッシュ
 * ズームイン/アウト時の再リクエストを防ぐ
 */
class TileCache {
  private readonly cache = new Map<string, string>(); // URL -> Object URL
  private readonly maxSize: number;

  constructor(maxSize = 300) {
    this.maxSize = maxSize;
  }

  get(url: string): string | undefined {
    const objectUrl = this.cache.get(url);
    if (objectUrl) {
      // LRU: アクセスされたアイテムを末尾に移動
      this.cache.delete(url);
      this.cache.set(url, objectUrl);
    }
    return objectUrl;
  }

  set(url: string, blob: Blob): string {
    // 既存のエントリがあれば古いObject URLを解放
    const existing = this.cache.get(url);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.cache.delete(url);
    }

    // サイズ上限を超えている場合、最も古いエントリを削除
    if (this.cache.size >= this.maxSize) {
      const firstEntry = this.cache.entries().next();
      if (!firstEntry.done) {
        const [oldestKey, oldestUrl] = firstEntry.value;
        URL.revokeObjectURL(oldestUrl);
        this.cache.delete(oldestKey);
      }
    }

    const objectUrl = URL.createObjectURL(blob);
    this.cache.set(url, objectUrl);
    return objectUrl;
  }
}

const tileCache = new TileCache(300);

// 出力プレビューの拡大キャプチャ時のように同時に大量タイルを取得すると、
// ブラウザの同時接続上限や瞬間的なネットワーク揺らぎで fetch が
// `TypeError: Failed to fetch` を投げることがある。
// 加えて、Zenrin API が HTTP 429 (Too Many Requests) を返したケースでは
// レスポンスに CORS ヘッダが付かないためブラウザは CORS ブロックとして
// 同じ TypeError を投げる。レート制限は秒単位以上で継続するため、
// バックオフ遅延を 250/750ms から 500/1500/3000ms に延ばし、3 回まで再試行する。
const NETWORK_RETRY_DELAYS_MS = [500, 1500, 3000] as const;

// Zenrin タイルの並列フェッチ上限。
// 高解像度キャプチャ時には ~50 枚のタイルを同時にリクエストするため、
// 無制限に流すと Zenrin 側のレート制限 (HTTP 429) を踏みやすい。
// 6 はブラウザの HTTP/1.1 同時接続上限の目安と一致する。
const MAX_CONCURRENT_TILE_FETCHES = 6;

let inFlightTileFetches = 0;
const tileFetchWaitQueue: Array<() => void> = [];

const acquireTileFetchSlot = async (): Promise<void> => {
  await new Promise<void>((resolve) => {
    if (inFlightTileFetches < MAX_CONCURRENT_TILE_FETCHES) {
      inFlightTileFetches += 1;
      resolve();
      return;
    }
    tileFetchWaitQueue.push(() => {
      inFlightTileFetches += 1;
      resolve();
    });
  });
};

const releaseTileFetchSlot = (): void => {
  inFlightTileFetches -= 1;
  const next = tileFetchWaitQueue.shift();
  if (next) {
    next();
  }
};

/**
 * タイル取得が中止されたかどうかを判定する。
 * @param signal fetch に渡した AbortSignal
 * @param error fetch または response.blob() で捕捉したエラー
 * @returns 中止時は `{ kind: "aborted" }`、それ以外は `null`
 */
function abortedTileResult(
  signal: AbortSignal | undefined,
  error: unknown
): { kind: "aborted" } | null {
  if (signal?.aborted) {
    return { kind: "aborted" };
  }
  if (isAbortError(error)) {
    return { kind: "aborted" };
  }
  return null;
}

/**
 * ゼンリン WMTS タイル画像を取得する（キャッシュ対応）
 * @param url 画像URL
 * @param options 取得オプション（AbortSignal、HTTPヘッダー、OAuthトークン、403時コールバック）
 * @description https://github.com/jaq316/leaflet-header
 */
export async function fetchZenrinTile(
  url: string,
  options: FetchZenrinTileOptions
): Promise<FetchZenrinTileResult> {
  const { signal, headers, oauthToken, onTokenInvalid } = options;

  const cachedObjectUrl = tileCache.get(url);
  if (cachedObjectUrl) {
    await new Promise<void>((resolve) => {
      queueMicrotask(() => {
        resolve();
      });
    });
    const aborted = abortedTileResult(signal, undefined);
    if (aborted) {
      return aborted;
    }
    return { kind: "success", objectUrl: cachedObjectUrl };
  }

  const _headers: Record<string, string> = {};
  if (headers) {
    headers.forEach((h) => {
      _headers[h.header] = h.value;
    });
  }
  if (oauthToken) {
    _headers.Authorization = `Bearer ${oauthToken}`;
  }

  await acquireTileFetchSlot();
  try {
    let response: Response | undefined;
    for (
      let attempt = 0;
      attempt <= NETWORK_RETRY_DELAYS_MS.length;
      attempt++
    ) {
      if (signal?.aborted) {
        return { kind: "aborted" };
      }

      try {
        response = await fetch(url, {
          method: "GET",
          headers: _headers,
          mode: "cors",
          signal,
        });
        break;
      } catch (error: unknown) {
        const aborted = abortedTileResult(signal, error);
        if (aborted) {
          return aborted;
        }
        if (attempt >= NETWORK_RETRY_DELAYS_MS.length) {
          console.error("ZenrinAPI 接続エラー (リトライ後)", error);
          Sentry.captureException(error);
          onTokenInvalid?.();
          return { kind: "failed" };
        }
        await new Promise<void>((resolve) => {
          setTimeout(resolve, NETWORK_RETRY_DELAYS_MS[attempt]);
        });
      }
    }

    if (!response) {
      return { kind: "failed" };
    }

    if (!response.ok) {
      if (response.status === 403) {
        const wwwAuthenticate = response.headers.get("Www-Authenticate");
        if (wwwAuthenticate?.includes("invalid_token") && onTokenInvalid) {
          onTokenInvalid();
        }
        return { kind: "failed", status: 403 };
      }
      console.error("ZenrinAPI HTTPエラー", response.statusText);
      Sentry.captureException(`ZenrinAPI HTTPエラー${response.statusText}`);
      return { kind: "failed", status: response.status };
    }

    let blob: Blob;
    try {
      blob = await response.blob();
    } catch (error: unknown) {
      const aborted = abortedTileResult(signal, error);
      if (aborted) {
        return aborted;
      }
      console.error("ZenrinAPI タイル body 読み取りエラー", error);
      Sentry.captureException(error);
      return { kind: "failed" };
    }

    const objectUrl = tileCache.set(url, blob);
    return { kind: "success", objectUrl };
  } finally {
    releaseTileFetchSlot();
  }
}
